import { supabase } from '@/lib/supabase';

export type CommandCenterAuthoritySessionStatus =
  | 'IDLE'
  | 'VERIFYING'
  | 'ALLOWED'
  | 'DENIED'
  | 'ERROR';

export interface CommandCenterAuthoritySessionSnapshot {
  readonly userId: string | null;
  readonly status: CommandCenterAuthoritySessionStatus;
  readonly allowed: boolean;
  readonly loading: boolean;
  readonly error: string | null;
  readonly verifiedAt: number | null;
  readonly generation: number;
}

export type CommandCenterAuthorityResolver =
  (
    userId: string,
  ) => Promise<boolean>;

export const COMMAND_CENTER_AUTHORITY_MAX_AGE_MS =
  15_000;

export const COMMAND_CENTER_AUTHORITY_PUBLIC_ERROR =
  'Command Center authority verification failed.';

export interface CommandCenterAuthoritySessionOptions {
  readonly now?: () => number;
  readonly maxAgeMs?: number;
}

export function isCommandCenterAuthoritySessionSnapshotFresh(
  snapshot:
    CommandCenterAuthoritySessionSnapshot,
  userId: string,
  now = Date.now(),
  maxAgeMs =
    COMMAND_CENTER_AUTHORITY_MAX_AGE_MS,
): boolean {
  return (
    snapshot.userId === userId &&
    (
      snapshot.status === 'ALLOWED' ||
      snapshot.status === 'DENIED'
    ) &&
    typeof snapshot.verifiedAt ===
      'number' &&
    now >= snapshot.verifiedAt &&
    now - snapshot.verifiedAt <=
      maxAgeMs
  );
}

export interface CommandCenterAuthoritySessionController {
  read:
    () => CommandCenterAuthoritySessionSnapshot;

  subscribe:
    (
      listener: () => void,
    ) => () => void;

  ensure:
    (
      userId: string,
    ) => Promise<CommandCenterAuthoritySessionSnapshot>;

  retry:
    (
      userId: string,
    ) => Promise<CommandCenterAuthoritySessionSnapshot>;

  reset:
    () => CommandCenterAuthoritySessionSnapshot;
}

const createIdleSnapshot =
  (
    generation: number,
  ): CommandCenterAuthoritySessionSnapshot =>
    Object.freeze({
      userId: null,
      status: 'IDLE',
      allowed: false,
      loading: false,
      error: null,
      verifiedAt: null,
      generation,
    });

export function createCommandCenterAuthoritySession(
  resolver:
    CommandCenterAuthorityResolver,
  options:
    CommandCenterAuthoritySessionOptions = {},
): CommandCenterAuthoritySessionController {
  const now =
    options.now ??
    (() => Date.now());

  const configuredMaxAgeMs =
    options.maxAgeMs;

  const maxAgeMs =
    typeof configuredMaxAgeMs === 'number' &&
    Number.isFinite(configuredMaxAgeMs) &&
    configuredMaxAgeMs > 0
      ? configuredMaxAgeMs
      : COMMAND_CENTER_AUTHORITY_MAX_AGE_MS;

  const listeners =
    new Set<() => void>();

  let generation =
    0;

  let snapshot =
    createIdleSnapshot(
      generation,
    );

  let inFlight:
    Promise<CommandCenterAuthoritySessionSnapshot> | null =
    null;

  let inFlightUserId:
    string | null =
    null;

  const notify =
    (): void => {
      for (const listener of listeners) {
        try {
          listener();
        } catch {
          // Subscriber failure cannot alter authority.
        }
      }
    };

  const publish =
    (
      next:
        CommandCenterAuthoritySessionSnapshot,
    ): CommandCenterAuthoritySessionSnapshot => {
      snapshot =
        Object.freeze({
          ...next,
        });

      notify();

      return snapshot;
    };

  const read =
    (): CommandCenterAuthoritySessionSnapshot =>
      snapshot;

  const subscribe =
    (
      listener: () => void,
    ): (() => void) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    };

  const beginResolution =
    (
      rawUserId: string,
      force: boolean,
    ): Promise<CommandCenterAuthoritySessionSnapshot> => {
      const userId =
        rawUserId.trim();

      if (!userId) {
        return Promise.resolve(
          reset(),
        );
      }

      if (
        inFlight !== null &&
        inFlightUserId === userId
      ) {
        return inFlight;
      }

      if (
        !force &&
        isCommandCenterAuthoritySessionSnapshotFresh(
          snapshot,
          userId,
          now(),
          maxAgeMs,
        )
      ) {
        return Promise.resolve(
          snapshot,
        );
      }

      generation += 1;

      const resolutionId =
        generation;

      const sessionKey =
        userId;

      publish({
        userId:
          sessionKey,
        status:
          'VERIFYING',
        allowed:
          false,
        loading:
          true,
        error:
          null,
        verifiedAt:
          null,
        generation:
          resolutionId,
      });

      const pendingResolution =
        Promise.resolve()
          .then(
            () =>
              resolver(
                sessionKey,
              ),
          )
          .then(
            allowed => {
              if (
                generation !==
                  resolutionId ||
                snapshot.userId !==
                  sessionKey
              ) {
                return snapshot;
              }

              return publish({
                userId:
                  sessionKey,
                status:
                  allowed
                    ? 'ALLOWED'
                    : 'DENIED',
                allowed,
                loading:
                  false,
                error:
                  null,
                verifiedAt:
                  now(),
                generation:
                  resolutionId,
              });
            },
          )
          .catch(
            (
              unknownError:
                unknown,
            ) => {
              if (
                generation !==
                  resolutionId ||
                snapshot.userId !==
                  sessionKey
              ) {
                return snapshot;
              }

              void unknownError;

              return publish({
                userId:
                  sessionKey,
                status:
                  'ERROR',
                allowed:
                  false,
                loading:
                  false,
                error:
                  COMMAND_CENTER_AUTHORITY_PUBLIC_ERROR,
                verifiedAt:
                  null,
                generation:
                  resolutionId,
              });
            },
          )
          .finally(
            () => {
              if (
                generation ===
                  resolutionId &&
                inFlightUserId ===
                  sessionKey
              ) {
                inFlight =
                  null;

                inFlightUserId =
                  null;
              }
            },
          );

      inFlight =
        pendingResolution;

      inFlightUserId =
        sessionKey;

      return pendingResolution;
    };

  const reset =
    (): CommandCenterAuthoritySessionSnapshot => {
      if (
        snapshot.status === 'IDLE' &&
        snapshot.userId === null &&
        inFlight === null
      ) {
        return snapshot;
      }

      generation += 1;

      inFlight =
        null;

      inFlightUserId =
        null;

      return publish(
        createIdleSnapshot(
          generation,
        ),
      );
    };

  return Object.freeze({
    read,
    subscribe,

    ensure:
      userId =>
        beginResolution(
          userId,
          false,
        ),

    retry:
      userId =>
        beginResolution(
          userId,
          true,
        ),

    reset,
  });
}

const resolveLiveCommandCenterAuthority:
  CommandCenterAuthorityResolver =
  async () => {
    const {
      data,
      error,
    } =
      await supabase
        .rpc(
          'has_command_center_access',
        );

    if (error) {
      throw new Error(
        COMMAND_CENTER_AUTHORITY_PUBLIC_ERROR,
      );
    }

    return data === true;
  };

const commandCenterAuthoritySession =
  createCommandCenterAuthoritySession(
    resolveLiveCommandCenterAuthority,
  );

export const readCommandCenterAuthoritySession =
  commandCenterAuthoritySession.read;

export const subscribeCommandCenterAuthoritySession =
  commandCenterAuthoritySession.subscribe;

export const ensureCommandCenterAuthoritySession =
  commandCenterAuthoritySession.ensure;

export const retryCommandCenterAuthoritySession =
  commandCenterAuthoritySession.retry;

export const resetCommandCenterAuthoritySession =
  commandCenterAuthoritySession.reset;
